/**
 * src/lib/studio-accent.js — applies a studio's chosen accent colour on the
 * public, unauthenticated pages (BookingPortal, FlashGallery, ConsentSign).
 *
 * Sets a RAW custom property, `--studio-accent-raw`, rather than `--accent`
 * itself — the actual, safety-clamped `--accent` a studio's colour resolves
 * to is derived by CSS in design-tokens/themes.js's generateCSS() (see its
 * comment on why that derivation can't live in a var() fallback). Setting
 * `--accent` directly here, as this file did before Phase 2, is exactly
 * what let a studio's raw hex reach real UI unchecked.
 */

/**
 * @param {string|null|undefined} hex The studio's `accent_color` (e.g.
 *   `"#1a1a1a"`). No-ops if falsy — the theme's own default accent applies.
 * @returns {void}
 */
export function applyStudioAccent(hex) {
  if (!hex) return;
  document.documentElement.style.setProperty('--studio-accent-raw', hex);
  document.documentElement.setAttribute('data-studio-accent', '');
}

/**
 * Undoes {@link applyStudioAccent} — call from a `useEffect` cleanup so
 * navigating away from a branded page doesn't leak its accent onto whatever
 * renders next.
 *
 * @returns {void}
 */
export function clearStudioAccent() {
  document.documentElement.style.removeProperty('--studio-accent-raw');
  document.documentElement.removeAttribute('data-studio-accent');
}
